// IMPORTANT: tabHistory MUST be imported before createRouter so its
// capture-phase popstate listener registers ahead of Vue Router's own
// popstate listener. Otherwise per-tab back/forward redirect breaks.
import { installTabHistory } from '@/helpers/tabHistory';
import { createWebHistory, createRouter } from "vue-router";
import routes from './routes';
import appConfig from "../../app.config";
import store from '@/state/store';
import api from '@/helpers/api';
import { storePermissions, canView, isAdmin, hasRole } from '@/helpers/permissions';
import { RoleNames } from '@/helpers/roleNames';
import { getModuleKey } from '@/state/modules/tabs';

/**
 * Path-prefix → permission form-group key.
 * Sentinels: '__admin__' (UserType=0 only), '__superadmin__' (SuperAdmin role only).
 * Order matters: longest prefix first to ensure specific paths beat shorter parents.
 */
const PREFIX_PERMISSION_MAP = [
  // Admin-only routes (UserType=0)
  ['/erp/users',                      '__admin__'],
  ['/erp/roles',                      '__admin__'],
  ['/erp/approval-config',            '__admin__'],
  ['/erp/dev-utility',                '__admin__'],
  ['/erp/project-settings',           '__admin__'],
  ['/erp/configuration/seeders',      '__superadmin__'],

  // Shared masters
  ['/erp/shared-ledgers',             'shared-ledgers'],
  ['/erp/shared-uom',                 'shared-uom'],
  ['/erp/shared-stock-items',         'shared-stock-items'],
  ['/erp/shared-stock-item-lookups',  'shared-stock-item-lookups'],
  ['/erp/shared-stock-groups',        'shared-stock-groups'],
  ['/erp/shared-categories',          'shared-categories'],
  ['/erp/shared-godowns',             'shared-godowns'],
  ['/erp/shared-price-lists',         'shared-price-lists'],
  ['/erp/shared-price-formulas',      'shared-price-formulas'],
  ['/erp/base-price-report',          'shared-price-lists'],

  // Master accounting (Ma)
  ['/erp/companies',                  'companies'],
  ['/erp/account-groups',             'account-groups'],
  ['/erp/ledgers',                    'ledgers'],
  ['/erp/financial-years',            'financial-years'],
  ['/erp/cost-categories',            'cost-categories'],
  ['/erp/cost-centers',               'cost-centers'],
  ['/erp/cost-center-summary',        'cost-centers'],
  ['/erp/account-settings',           'account-settings'],

  // Material / Inventory / Manufacturing (Mi)
  ['/erp/uom',                        'uom'],
  ['/erp/categories',                 'categories'],
  ['/erp/stock-item-lookups',         'stock-item-lookups'],
  ['/erp/stock-groups',               'stock-groups'],
  ['/erp/godowns',                    'godowns'],
  ['/erp/hsn-sac',                    'hsn-sac'],
  ['/erp/stock-items',                'stock-items'],
  ['/erp/stock-batches',              'stock-batches'],
  ['/erp/batch-recalls',              'batch-recalls'],
  ['/erp/heat-certificates',          'heat-certificates'],
  ['/erp/programs',                   'programs'],
  ['/erp/program-tolerances',         'program-tolerances'],
  ['/erp/quotations',                 'quotations'],
  ['/erp/sales-orders',               'sales-orders'],
  ['/erp/sale-programs',              'sale-programs'],
  ['/erp/sale-return-programs',       'sale-return-programs'],
  ['/erp/purchase-orders',            'purchase-orders'],
  ['/erp/purchase-programs',          'purchase-programs'],
  ['/erp/purchase-return-programs',   'purchase-return-programs'],
  ['/erp/purchase-mous',              'purchase-mous'],
  ['/erp/service-invoices',           'service-invoices'],
  ['/erp/service-bills',              'service-bills'],
  ['/erp/service-sale-orders',        'service-sale-orders'],
  ['/erp/service-purchase-orders',    'service-purchase-orders'],
  ['/erp/manufacturing-programs',     'manufacturing-programs'],
  ['/erp/gate-entry-checklist',       'gate-entry-checklist'],
  ['/erp/gate-entry',                 'gate-entry'],
  ['/erp/traceability',               'traceability'],

  // Stock reports
  ['/erp/stock-summary',              'stock-reports'],
  ['/erp/stock-grouped-summary',      'stock-reports'],
  ['/erp/stock-godown-summary',       'stock-reports'],
  ['/erp/stock-movement-analysis',    'stock-reports'],
  ['/erp/stock-ageing',               'stock-reports'],
  ['/erp/stock-unit-details',         'stock-reports'],

  // Store sub-module
  ['/erp/store/cfq',                          'store-cfq'],
  ['/erp/store/cs',                           'store-cs'],
  ['/erp/store/grn',                          'store-grn'],
  ['/erp/store/delivery-challan',             'store-delivery-challan'],
  ['/erp/store/material-issue',               'store-material-issue'],
  ['/erp/store/physical-stock-verification',  'store-physical-stock-verification'],
  ['/erp/store/gate-pass',                    'store-gate-pass'],
  ['/erp/store/purchase-orders',              'store-purchase-orders'],
  ['/erp/store/purchase-requisitions',        'store-purchase-requisitions'],
  ['/erp/store/sales-orders',                 'store-sales-orders'],
  ['/erp/store/vendor-scores',                'store-vendor-scores'],
  ['/erp/store/items',                        'store-items'],
  ['/erp/store/reports',                      'store-reports'],

  // Accounting / Vouchers / Tax (Ac)
  ['/erp/vouchers',                   'vouchers'],
  ['/erp/voucher-classes',            'voucher-classes'],
  ['/erp/approvals',                  'approvals'],
  ['/erp/ledger-classification',      'ledger-classification'],
  ['/erp/tax-rates',                  'tax-rates'],
  ['/erp/tds-tcs-sections',           'tds-tcs-sections'],
  ['/erp/tds-tcs-returns',            'tds-tcs-returns'],
  ['/erp/tds-tcs',                    'tds-tcs'],
  ['/erp/interest-masters',           'interest-masters'],
  ['/erp/gst-returns',                'gst-returns'],
  ['/erp/gstr9',                      'gstr9'],
  ['/erp/gst-filings',                'gst-filings'],
  ['/erp/itc-reconciliation',         'itc-reconciliation'],
  ['/erp/itc-ledger',                 'itc-ledger'],
  ['/erp/tax-challans',               'tax-challans'],
  ['/erp/tax-dashboard',              'tax-dashboard'],
  ['/erp/tax-calendar',               'tax-calendar'],
  ['/erp/eway-bill-config',           'eway-bill-config'],
  ['/erp/consolidated-eway-bills',    'consolidated-eway-bills'],
  ['/erp/eway-bills',                 'eway-bills'],

  // Accounting reports
  ['/erp/trial-balance',              'report-trial-balance'],
  ['/erp/day-book',                   'report-day-book'],
  ['/erp/balance-sheet',              'report-balance-sheet'],
  ['/erp/profit-loss',                'report-profit-loss'],
  ['/erp/insights',                   'insights'],
  ['/erp/reports',                    'vouchers'],
  ['/pharma/reports',                 'pharma-reports'],

  // Transport (Tp)
  ['/erp/tp/dashboard',               'tp-dashboard'],
  ['/erp/tp/vehicle-owner-invoices',  'tp-vehicle-owner-invoices'],
  ['/erp/tp/vehicle-owner-payments',  'tp-vehicle-owner-payments'],
  ['/erp/tp/vehicle-owners',          'tp-vehicle-owners'],
  ['/erp/tp/vehicles',                'tp-vehicles'],
  ['/erp/tp/drivers',                 'tp-drivers'],
  ['/erp/tp/driver-settlements',      'tp-driver-settlements'],
  ['/erp/tp/routes',                  'tp-routes'],
  ['/erp/tp/hubs',                    'tp-hubs'],
  ['/erp/tp/customers',               'tp-customers'],
  ['/erp/tp/brokers',                 'tp-brokers'],
  ['/erp/tp/rate-agreements',         'tp-rate-agreements'],
  ['/erp/tp/maintenance',             'tp-maintenance'],
  ['/erp/tp/fuel-bill-adjustments',   'tp-fuel-bill-adjustments'],
  ['/erp/tp/fuel-bills',              'tp-fuel-bills'],
  ['/erp/tp/fuel',                    'tp-fuel'],
  ['/erp/tp/consignment-notes',       'tp-consignment-notes'],
  ['/erp/tp/trip-sheets',             'tp-trip-sheets'],
  ['/erp/tp/freight-invoices',        'tp-freight-invoices'],
  ['/erp/tp/hire-charges',            'tp-hire-charges'],
  ['/erp/tp/warehouse',               'tp-warehouse'],
  ['/erp/tp/crm',                     'tp-crm'],
  ['/erp/tp/reports',                 'tp-reports'],
  ['/erp/tp/tyres',                   'tp-tyres'],
  ['/erp/tp/batteries',               'tp-batteries'],
  ['/erp/tp/masters',                 'tp-masters'],

  // Job Consultancy (Jc)
  ['/erp/jc/dashboard',               'jc-dashboard'],
  ['/erp/jc/employers',               'jc-employers'],
  ['/erp/jc/seekers',                 'jc-seekers'],
  ['/erp/jc/seekers/expert-verifications', 'jc-seekers'],
  ['/erp/jc/seekers/highlight-orders', 'jc-seekers'],
  ['/erp/jc/seekers/feedback-orders', 'jc-seekers'],
  ['/erp/jc/job-posts',               'jc-job-posts'],
  ['/erp/jc/saved-jobs',              'jc-saved-jobs'],
  ['/erp/jc/applications',            'jc-applications'],
  ['/erp/jc/interview-questions',     'jc-interviews'],
  ['/erp/jc/interviews',              'jc-interviews'],
  ['/erp/jc/panelist-requests',       'jc-panelist-requests'],
  ['/erp/jc/panelists',               'jc-panelists'],
  ['/erp/jc/candidate-selections',    'jc-candidate-selections'],
  ['/erp/jc/consultancy',             'jc-consultancies'],
  ['/erp/jc/wallet',                  'jc-wallet'],
  ['/erp/jc/plans',                   'jc-plans'],
  ['/erp/jc/payments',                'jc-payments'],
  ['/erp/jc/leads',                   'jc-leads'],
  ['/erp/jc/cms',                     'jc-cms'],
  ['/erp/jc/messages',                'jc-messages'],
  ['/erp/jc/reports',                 'jc-reports'],
  ['/erp/jc/users',                   'jc-users'],
  ['/erp/jc/masters',                 'jc-masters'],

  // Matrimony (Mm)
  ['/erp/mm/dashboard',               'mm-dashboard'],
  ['/erp/mm/profile-views',           'mm-profile-views'],
  ['/erp/mm/profiles',                'mm-profiles'],
  ['/erp/mm/photos',                  'mm-photos'],
  ['/erp/mm/documents',               'mm-documents'],
  ['/erp/mm/interests',               'mm-interests'],
  ['/erp/mm/messages',                'mm-messages'],
  ['/erp/mm/blocks',                  'mm-blocks'],
  ['/erp/mm/shortlists',              'mm-shortlists'],
  ['/erp/mm/matching',                'mm-matching'],
  ['/erp/mm/notifications',           'mm-notifications'],
  ['/erp/mm/subscriptions',           'mm-subscriptions'],
  ['/erp/mm/payments',                'mm-payments'],
  ['/erp/mm/plans',                   'mm-plans'],
  ['/erp/mm/vendor-categories',       'mm-vendor-categories'],
  ['/erp/mm/vendors',                 'mm-vendors'],
  ['/erp/mm/advisors',                'mm-advisors'],
  ['/erp/mm/horoscope-requests',      'mm-horoscope-requests'],
  ['/erp/mm/horoscope',               'mm-horoscope'],
  ['/erp/mm/kundali',                 'mm-kundali'],
  ['/erp/mm/video-calls',             'mm-video-calls'],
  ['/erp/mm/reports',                 'mm-reports'],
  ['/erp/mm/masters',                 'mm-masters'],

  // POS
  ['/erp/pos/orders',                 'pos-orders'],
  ['/erp/pos/sessions',               'pos-sessions'],
  ['/erp/pos/returns',                'pos-returns'],

  // Website CMS
  ['/erp/website/blog',               'website-blog'],
  ['/erp/website/faqs',               'website-faqs'],
  ['/erp/website/events',             'website-events'],
  ['/erp/website/contacts',           'website-contacts'],
  ['/erp/website/gallery',            'website-gallery'],

  // Cross-cutting / Admin (admin-bypass via UserType, key kept for UI gating)
  ['/erp/documents',                  'documents'],
  ['/erp/document-types',             'document-types'],
  ['/erp/document-numbering',         'document-numbering'],
  ['/erp/document-field-mappings',    'document-field-mappings'],
  ['/erp/email-templates',            'email-templates'],
  ['/erp/audit-logs',                 'audit-logs'],
  ['/erp/activity-log',               'activity-log'],
  ['/erp/system-settings',            'system-settings'],
  ['/erp/notifications',              'notifications'],
  ['/erp/bulk-promote',               'bulk-promote'],
  ['/erp/chatbot',                    'chatbot'],
  ['/erp/document-ai',                'document-ai'],
];

// Sort once at module load: longest prefix first so '/erp/sales-orders/123' beats '/erp/sales' if both exist.
PREFIX_PERMISSION_MAP.sort((a, b) => b[0].length - a[0].length);

function getPermissionKey(path) {
  for (const [prefix, key] of PREFIX_PERMISSION_MAP) {
    if (path === prefix || path.startsWith(prefix + '/')) return key;
  }
  return null;
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

// API based Authentication — IntegralAPI
//
// Profile cache:
//   /auth/profile is the source of truth for permissions, project settings,
//   account settings, and JWT freshness. Hitting it on every route change
//   adds a sub-second round-trip per nav. We cache the response for a short
//   TTL so most navigations skip the network call. Revoked permissions and
//   disabled modules propagate within PROFILE_CACHE_TTL_MS at most.
const PROFILE_CACHE_TTL_MS = 30000; // 30s — tune per security posture
let lastProfileFetchAt = 0;

// Reset the cache after auth state transitions (login / logout / company switch)
// so the next nav refetches profile with the new identity.
export function invalidateProfileCache() {
  lastProfileFetchAt = 0;
}

function runRouteGuards(routeTo, next) {
  const permKey = getPermissionKey(routeTo.path);
  const isSentinel = permKey === '__admin__' || permKey === '__superadmin__';

  // Project-settings route guard — check if form/module is enabled for this installation
  if (permKey && !isSentinel && !store.getters['projectSettings/isFormEnabled'](permKey)) {
    return next({ name: 'default' });
  }

  // Permission-based route guard
  if (permKey === '__admin__' && !isAdmin()) {
    return next({ name: 'default' });
  }
  if (permKey === '__superadmin__' && !hasRole(RoleNames.SuperAdmin) && !isAdmin()) {
    return next({ name: 'default' });
  }
  if (permKey && !isSentinel && !canView(permKey)) {
    return next({ name: 'default' });
  }
  return next();
}

router.beforeEach(async (routeTo, routeFrom, next) => {
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  if (!authRequired) return next();

  const token = localStorage.getItem('jwt');
  if (!token) {
    const loginRoute = routeTo.meta.loginRoute || routeTo.matched.find(r => r.meta.loginRoute)?.meta.loginRoute;
    if (loginRoute) {
      return next({ path: loginRoute, query: { redirectFrom: routeTo.fullPath } });
    }
    return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  }

  // Skip the profile round-trip if we fetched recently. Permission/route
  // checks below still run against the cached store/localStorage data.
  const now = Date.now();
  if (lastProfileFetchAt && now - lastProfileFetchAt < PROFILE_CACHE_TTL_MS) {
    return runRouteGuards(routeTo, next);
  }

  try {
    const { data } = await api.get('/auth/profile');
    lastProfileFetchAt = now;
    localStorage.setItem('userdata', JSON.stringify(data.user));
    localStorage.setItem('userid', String(data.user.id));
    if (data.permissions) storePermissions(data.permissions);
    if (data.projectSettings) {
      store.commit('projectSettings/SET_SETTINGS', data.projectSettings);
    }
    if (data.accountSettings) {
      store.commit('accountSettings/SET_SETTINGS', data.accountSettings);
    }
    localStorage.setItem('user', JSON.stringify(data.user));

    return runRouteGuards(routeTo, next);
  } catch (err) {
    // Only clear JWT and redirect to login on actual 401 (token expired/invalid).
    // Network errors (timeout, server down) should NOT log the user out —
    // the token may still be valid; the server is just unreachable.
    const is401 = err?.response?.status === 401;
    if (is401) {
      localStorage.removeItem('jwt');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      const loginRoute = routeTo.meta.loginRoute || routeTo.matched.find(r => r.meta.loginRoute)?.meta.loginRoute;
      if (loginRoute) {
        return next({ path: loginRoute, query: { redirectFrom: routeTo.fullPath } });
      }
      return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
    }
    // Network error / timeout — let the user through with cached auth data
    next();
  }
});

router.beforeEach((routeTo, routeFrom, next) => {
  if (process.env.VUE_APP_DEFAULT_AUTH === "firebase") {

    // Check if auth is required on this route
    // (including nested routes).
    const authRequired = routeTo.matched.some((route) => route.meta.authRequired);

    // If auth isn't required for the route, just continue.
    if (!authRequired) return next();

    // If auth is required and the user is logged in...
    if (store.getters['auth/loggedIn']) {
      // Validate the local user token...
      return store.dispatch('auth/validate').then((validUser) => {
        // Then continue if the token still represents a valid user,
        // otherwise redirect to login.
        validUser ? next() : redirectToLogin();
      });
    }

    // If auth is required and the user is NOT currently logged in,
    // redirect to login.
    redirectToLogin();

    // eslint-disable-next-line no-unused-vars
    // eslint-disable-next-line no-inner-declarations
    function redirectToLogin() {
      // Pass the original route to the login component
      next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
    }
  } else {
    const publicPages = ['/login', '/register', '/forgot-password', '/mobile/login'];
    const authpage = !publicPages.includes(routeTo.path);
    const loggeduser = localStorage.getItem('user');

    if (authpage && !loggeduser) {
      if (routeTo.path.startsWith('/mobile/')) {
        return next('/mobile/login');
      }
      return next('/login');
    }

    next();
  }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + ' | ' + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

// Menu-search recents — track visited menu paths so the Ctrl+K palette can
// surface "Recently visited" when query is empty. Stored in localStorage,
// capped at 20 entries, deduped by path.
const RECENTS_KEY = "menuSearch.recent";
const RECENTS_CAP = 20;
router.afterEach((to, from, failure) => {
  if (failure) return;
  if (!to.matched.some((r) => r.meta.authRequired)) return;
  if (to.meta.workspaceTab === false) return;
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(arr) ? arr.filter((p) => p !== to.fullPath) : [];
    list.unshift(to.fullPath);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_CAP)));
  } catch {
    // localStorage full or disabled — non-fatal
  }
});

// Workspace Tabs — auto-open a tab chip on each authenticated navigation
// Tab key = module base path (e.g. /erp/ledgers for all ledger sub-routes)
// Sub-routes (create, edit/:id) update the same tab instead of opening new ones.
router.afterEach((to, from, failure) => {
  // Vue Router 4 fires afterEach even on cancelled/failed navigations
  // (next(false), guard rejects, etc.). Skip — otherwise tab state mutates
  // for navigations that never completed (e.g. cross-tab popstate redirect).
  if (failure) return;
  if (!store.state.tabs?.enabled) return;
  const authRequired = to.matched.some((r) => r.meta.authRequired);
  if (!authRequired) return;
  if (to.meta.workspaceTab === false) return;

  const moduleKey = getModuleKey(to.fullPath);
  const title = to.meta.title || to.name || 'Page';

  store.dispatch('tabs/openTab', {
    key: moduleKey,
    path: to.fullPath,
    title: title,
    icon: to.meta.icon || 'ri-pages-line',
    componentName: null,
  });
});

// Per-tab browser-back integration. Installed after the openTab afterEach
// hook so the active tab key is up-to-date when we stamp history state —
// router.afterEach handlers fire in registration order.
installTabHistory(router, store);

export default router;
